import AIRefineIcon from '@assets/icons/AIRefineIcon'
import UndoIcon from '@assets/icons/UndoIcon'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import useUpgradeLicense from '@shared/licensing/useUpgradeLicense'
import { AppPermission } from '@shared/permissions'
import { T, useT } from '@transifex/react'

export type RefineWithAIButtonProps = {
  /** Whether the message field is empty, computed by the caller (rich text vs plain text). */
  isMessageEmpty: boolean
  hasRefined: boolean
  isPendingRefineMessage: boolean
  isPendingUndoRefinedMessage: boolean
  onBeforeRefine: () => void | Promise<unknown>
  onRefine: () => void | Promise<unknown>
  onUndo: () => void | Promise<unknown>
  refineButtonId: string
  undoButtonId: string
}

/**
 * Presentational "Refine with AI" button (plus undo affordance) shared across
 * entities. Entity-specific refinement state is supplied by the caller, which
 * wires up its own `useXRefinement` hook. The upgrade-license interception is
 * shared because it is identical for every entity.
 */
const RefineWithAIButton: React.FC<RefineWithAIButtonProps> = ({
  isMessageEmpty,
  hasRefined,
  isPendingRefineMessage,
  isPendingUndoRefinedMessage,
  onBeforeRefine,
  onRefine,
  onUndo,
  refineButtonId,
  undoButtonId,
}) => {
  const t = useT()
  const { interceptClick } = useUpgradeLicense()

  const handleClick = (event: React.MouseEvent) => {
    interceptClick(
      event as React.MouseEvent<HTMLAnchorElement, MouseEvent>,
      hasAppPermission =>
        hasAppPermission('refinementApp', AppPermission.RefinementAppUpgradeLicense),
      async () => {
        await onBeforeRefine()
        await onRefine()
      },
    )
  }

  const handleUndo = async () => {
    await onUndo()
    // Restoring the message updates Froala via form state; that can move focus back into
    // the contenteditable and reopen the keyboard on iOS. Blur after undo completes.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }

  return (
    <Stack
      direction="row"
      useFlexGap
      sx={{
        alignItems: 'center',
        gap: 1,
      }}>
      <Button
        id={refineButtonId}
        disabled={isMessageEmpty || isPendingRefineMessage || isPendingUndoRefinedMessage}
        onClick={handleClick}
        variant="text"
        startIcon={<AIRefineIcon />}>
        <T _str="Refine with AI" />
      </Button>
      {hasRefined && (
        <Button
          onClick={handleUndo}
          aria-label={t('Undo refine')}
          color="primary"
          variant="text"
          onPointerDown={e => e.preventDefault()}
          onMouseDown={e => e.preventDefault()}
          onTouchStart={e => e.preventDefault()}
          disabled={isPendingUndoRefinedMessage || isPendingRefineMessage}
          id={undoButtonId}>
          <UndoIcon />
        </Button>
      )}
    </Stack>
  )
}

export default RefineWithAIButton
